import { createI18n } from 'vue-i18n';

import { escapeHtml } from '@/common/helpers/report-theme.helper';
import en from '@/common/locales/en.json';
import es from '@/common/locales/es.json';
import fr from '@/common/locales/fr.json';
import pt from '@/common/locales/pt.json';
import { getLanguage, Language, missingTranslationHandler } from '@/common/modules';

/**
 * The app-wide vue-i18n instance. Extracted from `main.ts` so non-component code
 * (notably the pure PDF-report builder helpers, which have no `$t`) can reach the
 * message catalogs via `i18n.global.t`.
 */
export const i18n = createI18n({
  legacy: false,
  locale: getLanguage(),
  fallbackLocale: Language.EN_US,
  messages: {
    [Language.EN_US]: en,
    [Language.ES_ES]: es,
    [Language.FR_FR]: fr,
    [Language.PT_BR]: pt,
  },
  missingWarn: true,
  globalInjection: true,
  missing: missingTranslationHandler,
});

/**
 * A locale-bound translator for report output. The app uses the English string as
 * the message key, so a report builder calls `t('Area of interest')` and gets the
 * string back in the report's chosen language — independent of the active UI
 * locale, via vue-i18n's per-call `{ locale }` override.
 *
 * `html` is the escaped variant for interpolation into HTML report bodies;
 * `formatDateLong` / `formatDateShort` own locale-aware date formatting so callers
 * never repeat the `Intl` option bags.
 */
export interface ReportTranslator {
  (key: string, params?: Record<string, unknown>): string;
  /** Escaped translation, safe to interpolate into report HTML. */
  html: (key: string, params?: Record<string, unknown>) => string;
  /** Long human date; day/month/year order follows the report locale (e.g. en-US
   * "July 6, 2026", es-ES "6 de julio de 2026"). Local time zone. */
  formatDateLong: (date: Date) => string;
  /** Short day label in the report locale (e.g. en-US "Jul 6"). UTC. */
  formatDateShort: (date: Date) => string;
}

export const makeReportTranslator = (locale: Language): ReportTranslator => {
  const t = ((key: string, params?: Record<string, unknown>) =>
    i18n.global.t(key, params ?? {}, { locale })) as ReportTranslator;
  t.html = (key, params) => escapeHtml(t(key, params));
  t.formatDateLong = (date) =>
    date.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
  t.formatDateShort = (date) =>
    date.toLocaleDateString(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return t;
};
